import { SILHOUETTE_DEFAULT_T, type SilhouetteBoxesCm, type VariantPricing } from "@pufimi/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cartQuoteQuery } from "@/features/cart/cartQuote";
import { GenerationLoader, MIN_LOADER_MS } from "@/features/jobs/components/GenerationLoader";
import { useJobStream } from "@/features/jobs/hooks/useJobStream";
import { formatPriceZl } from "@/features/orders/pricing";
import ArEntryButton from "@/features/viewer/ar/ArEntryButton";
import type { ArSource } from "@/features/viewer/ar/arLaunch";
import { useArCapability } from "@/features/viewer/ar/useArCapability";
import LegalNotice from "@/features/viewer/components/LegalNotice";
import StageControls, { STAGE_SLIDER_STEP } from "@/features/viewer/components/StageControls";
import { usePrefersReducedMotion } from "@/features/viewer/hooks/usePrefersReducedMotion";
import { useStaticRectangles } from "@/features/viewer/hooks/useStaticRectangles";
import { resolvePillowMeshUrl } from "@/features/viewer/resolvePillowMesh";
import { cushionReadout } from "@/features/viewer/sizeModel";
import { useStageStore } from "@/features/viewer/stageStore";
import { capture, captureOnce } from "@/lib/analytics";
import { api } from "@/lib/api";
import { clientConfigQuery } from "@/lib/clientConfig";
import { i18n } from "@/lib/i18n";
import { appShellTitle } from "@/lib/seo/head";
import { cn } from "@/lib/utils";
import { useCartStore } from "@/stores/useCartStore";

// The pillow base colour, revealed wherever the pet doesn't cover the front
// (ADR-0033 §3). Applied to the RECTANGLE only; the silhouette mesh IS the pet
// shape, so it takes the pet edge-to-edge with no base fill.
const RECTANGLE_BACKGROUND_COLOR = "#ffffff";

// The preview is now DOM CHROME ONLY. The pillow lives on the ONE persistent
// Stage canvas (StageHost, in __root): while the worker runs, that canvas holds
// the empty lit warm sweep (the waiting state); the moment the finale assets land
// we push them into the Stage store and the materialize-at-rest reveal plays in
// the SAME scene the front door's drop flew into — no remount, no blink.
function PreviewShell() {
  const { t } = useTranslation();
  // The "Zamów" CTA copy lives in the orders namespace alongside the rest of the
  // checkout funnel (#228).
  const { t: tOrders } = useTranslation("orders");
  // Viewer namespace for the Stage-side notices (the mesh-load error below).
  const { t: tViewer } = useTranslation("viewer");
  const { jobId } = Route.useParams();
  const stream = useJobStream(jobId);

  // Render-time placement (ADR-0033 §1): shape + rectangle size live in the Stage
  // store so the SAME flat-chrome controls bar carries them on the upload Stage and
  // here (#163 Option A) — the choice persists across upload → preview.
  const shape = useStageStore((s) => s.shape);
  const size = useStageStore((s) => s.size);
  const setShape = useStageStore((s) => s.setShape);
  const setSize = useStageStore((s) => s.setSize);
  const rectangleUrls = useStaticRectangles();
  const resetView = useStageStore((s) => s.resetView);
  // The Orbit toggle (ADR-0046 §2): the mode switch between the primary pan drag and
  // free rotation. In the store (like the rest of the Stage settings) — the toolbar/
  // corner button flips it here, the persistent Stage canvas reads it.
  const orbitMode = useStageStore((s) => s.orbitMode);
  const setOrbitMode = useStageStore((s) => s.setOrbitMode);
  // In-viewer size selection (#185): the silhouette slider position lives in the
  // Stage store (like shape/size). cushionExtent is the cushion's measured face dims,
  // reported out of the persistent canvas by LivingPillow — together they feed both
  // the slider and the cm caption.
  const silhouetteSizeT = useStageStore((s) => s.silhouetteSizeT);
  const setSilhouetteSizeT = useStageStore((s) => s.setSilhouetteSizeT);
  const cushionExtent = useStageStore((s) => s.cushionExtent);
  // Drives the legal notice's post-Reveal peek: the same bump that plays the
  // Reveal (a pillow first landing) starts the auto-expand timer.
  const revealPlayToken = useStageStore((s) => s.revealPlayToken);
  // Gate the controls on the light-led reveal (#184, ADR-0041 §2): they fade in
  // ONLY after the reveal settles, never sharing its emotional peak. Hidden while a
  // fresh reveal is mid-play (view === "pillow" && !revealComplete); shown during
  // the WAIT (the ghost sweep — settings still pickable) and after the reveal. Under
  // reduced motion there's no animation, so they're present from the start (snap).
  const view = useStageStore((s) => s.view);
  const revealComplete = useStageStore((s) => s.revealComplete);
  // A failed mesh/texture load (a 404 / reaped URL) flips this; the Stage shows the
  // empty sweep in place of the broken cushion (never a crashed page — the old
  // behaviour bubbled to the app error panel with no recovery). We surface a quiet
  // toast so the user knows WHY the cushion vanished and that picking another shape
  // recovers. Firing on the false→true transition keeps it to one toast per failure.
  const meshError = useStageStore((s) => s.meshError);
  useEffect(() => {
    if (meshError) toast.error(tViewer("errors.meshLoad"));
  }, [meshError, tViewer]);
  const reducedMotion = usePrefersReducedMotion();
  // Once the FIRST reveal has settled, keep the chrome up for good. A later shape/size
  // swap re-materializes the cushion (showPillow resets revealComplete for the fresh
  // beat), but the controls must NOT vanish every time the user toggles shape (Leon,
  // #ui-polish — "rectangle makes the toolbar disappear"). The gate only hides them
  // during that first materialize; after that this latch keeps them present.
  const [hasRevealed, setHasRevealed] = useState(false);
  useEffect(() => {
    if (revealComplete) setHasRevealed(true);
  }, [revealComplete]);
  const chromeVisible = reducedMotion || hasRevealed || revealComplete || view !== "pillow";

  const meshUrl = resolvePillowMeshUrl({
    shape,
    size,
    meshSilhouetteUrl: stream.assets.meshSilhouette,
    rectangleUrls,
  });
  const isRectangle = shape === "rectangle";
  // The pet texture_front is the "can we composite yet?" signal.
  const ready = Boolean(stream.assets.textureFront && meshUrl);

  // AR entry point (#382, ADR-0035 §6/§7): the device's AR capability (probed once)
  // + the client-safe launch-flag state gate the "Zobacz w swoim pokoju" button. The
  // button itself self-hides unless capability is `native`, the flag is on, AND the
  // pillow is READY. Fails hidden — a failed/blocked config read leaves it false, so
  // the button never renders broken.
  const arCapability = useArCapability();
  const arPreviewEnabled = useQuery(clientConfigQuery()).data?.arPreviewEnabled ?? false;

  const failed = stream.status === "failed";
  const router = useRouter();
  const queryClient = useQueryClient();

  // Funnel (ADR-0050): `preview_ready` when the generated pillow (mesh + pet)
  // first lands; `configurator_opened` when the controls settle in and become
  // usable after the reveal. Each fires once per job (dedup-keyed); jobId is an
  // id, not PII.
  const configuratorOpen = ready && (reducedMotion || hasRevealed || revealComplete);
  useEffect(() => {
    if (ready) captureOnce(`preview_ready:${jobId}`, "preview_ready", { jobId });
  }, [ready, jobId]);
  useEffect(() => {
    if (configuratorOpen)
      captureOnce(`configurator_opened:${jobId}`, "configurator_opened", { jobId });
  }, [configuratorOpen, jobId]);

  // Graceful degradation: a reaped / expired / not-ours job (the ~7-day TTL) makes the
  // stream 404 / 403. Show the quiet empty Stage — never a hung loader or an error
  // screen. The loader is suppressed below via `deadPointer` so the warm sweep shows.
  const deadPointer = stream.error?.code === "not_found" || stream.error?.code === "forbidden";
  useEffect(() => {
    if (deadPointer) useStageStore.getState().showGhost();
  }, [deadPointer]);

  // Retry after a failed generation always starts fresh at /upload — "try another photo".
  // (The browser-local Resume pointer is retired, ADR-0056; the cart holds prior work.)
  const onRetry = () => {
    void router.navigate({ to: "/upload" });
  };

  // Minimum-wait floor (#ui-polish, Leon): even if the worker finishes the whole
  // pipeline in under a second, hold the reveal until the loader has had time to play
  // its full paced step sequence (MIN_LOADER_MS). Without this the bar would strobe and
  // snap straight to the cushion; with it the wait always reads as a calm, legible
  // progression. In the common case the final 3D step runs longer than the floor, so
  // this never adds delay — it only smooths the fast path. Failure is unaffected: it
  // sets `ready` false, so the loader (which shows its own error variant) stays up.
  const [minElapsed, setMinElapsed] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setMinElapsed(true), MIN_LOADER_MS);
    return () => window.clearTimeout(id);
  }, []);
  // The floor only applies to a LIVE generation. If we open a job that's already done
  // (a saved/shared preview link, or a revisit), the assets land on the first poll and
  // we never observe it queued/running — skip the fake wait and reveal the model at once
  // (Leon, #ui-polish). We latch "saw it in progress" so a fresh job that briefly shows
  // running keeps the floor even after it finishes.
  const sawInProgressRef = useRef(false);
  if (!ready && (stream.status === "queued" || stream.status === "running")) {
    sawInProgressRef.current = true;
  }
  const revealReady = ready && (minElapsed || !sawInProgressRef.current);

  // The generation loader owns the screen while the worker runs (or on failure). It
  // fades out as the finished cushion reveals; we keep it mounted through the fade so
  // the loop→reveal handoff crossfades instead of popping. Once ready, unmount after
  // the 500ms fade so its animation stops (nothing runs behind the hero).
  //
  // everReady latches on the first ready cushion: after that, swapping to a shape whose
  // mesh isn't loaded yet (e.g. a not-yet-seeded rectangle GLB) must NOT slam the
  // full-screen loader back over the revealed pillow — hold the last-good cushion.
  const [everReady, setEverReady] = useState(false);
  useEffect(() => {
    if (revealReady) setEverReady(true);
  }, [revealReady]);
  // A dead pointer (404 / 403) shows the quiet empty Stage, not a hung loader.
  const showLoader = !revealReady && !everReady && !deadPointer;
  const [loaderMounted, setLoaderMounted] = useState(true);
  useEffect(() => {
    if (showLoader) {
      setLoaderMounted(true);
      return;
    }
    const id = window.setTimeout(() => setLoaderMounted(false), 500);
    return () => window.clearTimeout(id);
  }, [showLoader]);

  // The single source of truth (#185) for the cm caption + S/L tier — branched by
  // shape, the SAME sizeModel that drives the applied 3D scale in the host, so the
  // number, the tier committed on "Zamów" and the cushion can't drift apart. Null for
  // the silhouette until its mesh is measured. Operator-editable shipping boxes (cm),
  // fetched alongside the tier-pricing below (#245, extended 2026-07-12). undefined
  // until fetched → the caption/tier fall back to the shared defaults; the 3D scale
  // never depends on them.
  const [boxes, setBoxes] = useState<SilhouetteBoxesCm | undefined>(undefined);
  const readout = cushionReadout({
    shape,
    silhouetteT: silhouetteSizeT,
    size,
    extent: cushionExtent,
    boxes,
  });

  // Variant pricing (#228, generalized 2026-07-22): the catalogue base prices for BOTH
  // shapes, fetched once the project id is known. Feeds the always-visible price in the
  // "Zamów" CTA (art. 4 ustawy o cenach — the price is shown where the pillow is offered,
  // not only at checkout) and the silhouette slider's box geometry. Shape is a client-
  // side pre-commit choice, so the response carries both shapes; no refetch on a toggle.
  // A missing/failed price just leaves the CTA priceless — it never blocks sizing.
  const projectIdForPricing = stream.projectId;
  const [pricing, setPricing] = useState<VariantPricing | null>(null);
  useEffect(() => {
    if (!projectIdForPricing) {
      setPricing(null);
      return;
    }
    let cancelled = false;
    void api.checkout
      .variantPricing(projectIdForPricing)
      .then((result) => {
        if (cancelled) return;
        setPricing(result);
        const { maxLongCm, maxShortCm, smallLongCm, smallShortCm } = result.silhouetteBoxes;
        setBoxes({ big: [maxLongCm, maxShortCm], small: [smallLongCm, smallShortCm] });
      })
      .catch(() => {
        if (!cancelled) setPricing(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectIdForPricing]);

  // Open the silhouette slider ONE STEP past the S/L boundary (`readout.markerT`) —
  // the smallest size that clears the threshold into the large (surcharge) tier, so
  // that larger size is the automatic default instead of the static mid-range
  // constant. One slider step (STAGE_SLIDER_STEP) past the marker keeps the thumb
  // unambiguously on the large side of the line rather than sitting exactly on it.
  // `markerT` is photo-dependent (needs the measured mesh + boxes), so it can't seed
  // the store default at init; we do it here the first time it's known. Guarded so it
  // only fires while the slider is still on the untouched constant default — never
  // clobbering a size the buyer already dragged to (or picked on the upload Stage,
  // where the position persists into this route).
  const seededSilhouetteDefaultRef = useRef(false);
  const markerT = readout?.markerT;
  useEffect(() => {
    if (seededSilhouetteDefaultRef.current) return;
    if (shape !== "silhouette" || typeof markerT !== "number") return;
    seededSilhouetteDefaultRef.current = true;
    if (silhouetteSizeT === SILHOUETTE_DEFAULT_T) {
      setSilhouetteSizeT(Math.min(1, markerT + STAGE_SLIDER_STEP));
    }
  }, [shape, markerT, silhouetteSizeT, setSilhouetteSizeT]);

  // "Zamów" (#228): the size the buyer chose here is the order's size. Commit it
  // to the project (shape + pricing tier + the exact cm footprint the caption
  // shows) BEFORE leaving for checkout — checkout is read-only and just displays
  // it. `readout` carries the same cm as the on-screen caption, so what you saw
  // is what you order. Disabled until the cushion is measured (`readout` set).
  const projectId = stream.projectId;
  const [ordering, setOrdering] = useState(false);
  const canOrder = ready && Boolean(projectId) && readout !== null && !ordering;
  async function onOrder() {
    if (!canOrder || !projectId || !readout) return;
    setOrdering(true);
    try {
      const fresh = await api.checkout.commitSelection(projectId, {
        shape,
        // The tier comes from the SAME readout the caption shows — for the silhouette
        // it's which physical box the chosen size fits (readout.tier), so what you saw
        // is what you're billed.
        size: readout.tier,
        dimensionsCm: { width: readout.widthCm, height: readout.heightCm },
      });
      // Add the just-created Product to the browser-local Cart (ADR-0054 §2): "Zamów"
      // adds the product and navigates to the cart (the checkout page), it no longer
      // goes straight to Stripe. The commit created/resolved the Product server-side;
      // `fresh.productId` is its id (idempotent — a re-Zamów never inflates the cart).
      if (fresh.productId) useCartStore.getState().ensureProduct(fresh.productId);
      // Warm the cart quote for the just-added cart so /checkout paints its summary +
      // thumbnail instantly on arrival — the SAME react-query key the checkout page
      // reads (#345, now cart-keyed since checkout is project-agnostic, ADR-0056). A
      // failed warm never blocks — checkout fetches on its own.
      void queryClient.prefetchQuery(cartQuoteQuery(useCartStore.getState().items)).catch(() => {});
      // Funnel (ADR-0050): the buyer committed a size via "Zamów". Enum + cm
      // amounts only — no PII.
      capture("size_selected", {
        shape,
        size: readout.tier,
        widthCm: readout.widthCm,
        heightCm: readout.heightCm,
      });
      // Uniform boundary (ADR-0052 baseline): always a plain navigation, so the
      // checkout crossing is the full-page root cross-fade — no element morph, no
      // position jump. The cart (not the URL) carries what's ordered (ADR-0056).
      await router.navigate({ to: "/checkout" });
    } catch {
      toast.error(tOrders("checkout.orderError"));
      setOrdering(false);
    }
  }

  // Hand the resolved pillow to the persistent Stage. While not ready, the Stage
  // keeps showing the ghost (the waiting state) — we don't touch it.
  const textureFront = stream.assets.textureFront;
  const textureBack = stream.assets.textureBack;
  useEffect(() => {
    if (!revealReady || !meshUrl) return;
    useStageStore.getState().showPillow({
      meshUrl,
      textureFrontUrl: textureFront,
      textureBackUrl: isRectangle ? undefined : textureBack,
      petBackgroundColor: isRectangle ? RECTANGLE_BACKGROUND_COLOR : undefined,
    });
  }, [revealReady, meshUrl, textureFront, textureBack, isRectangle]);

  // The "Zamów" conversion CTA (#228), now handed to the controls bar so it reads as
  // the bar's primary action (Leon, #ui-polish — it was a detached top-right button
  // that the mobile menu overlapped). The coral fill sets it apart from the quiet ghost
  // controls; min-h-11 keeps it on the bar's height baseline. It COMMITS the chosen size
  // before navigating (checkout is read-only), so it owns a brief loading state. Built
  // here (the commit logic lives here); StageControls only places it.
  //
  // The absolute price of the CURRENT configuration (#228): the buyer has already picked
  // a size, so `readout.tier` (S/M/L, the same key checkout bills on) indexes the fetched
  // prices. Silhouette only ever resolves to S/L. Shown in the CTA so the pillow's price
  // is visible on the viewer; null until priced, so the CTA falls back to a bare "Zamów".
  let currentPriceCents: number | undefined;
  if (pricing && readout) {
    if (shape === "silhouette") {
      currentPriceCents =
        readout.tier === "L" ? pricing.pricesCents.silhouette.L : pricing.pricesCents.silhouette.S;
    } else {
      currentPriceCents = pricing.pricesCents.rectangle[readout.tier];
    }
  }
  const orderPriceLabel = currentPriceCents != null ? formatPriceZl(currentPriceCents) : null;
  const orderCta =
    ready && projectId ? (
      <Button
        type="button"
        // Solid coral (the reserved primary — the segmented toggles are now a lighter
        // tint) + a slightly stronger lift, so it reads as THE action of the bar rather
        // than another toggle (Leon, #ui-polish). Height matches the bar's tallest item
        // (the shape switch, ~3.25rem) so the whole rail sits on one line.
        className="min-h-[3.25rem] rounded-full px-12 text-sm font-semibold shadow-md hover:shadow-lg"
        disabled={!canOrder}
        aria-busy={ordering}
        onClick={() => void onOrder()}
        data-testid="preview-order-cta"
      >
        {ordering
          ? tOrders("checkout.ctaBusy")
          : orderPriceLabel
            ? tOrders("checkout.ctaWithPrice", { price: orderPriceLabel })
            : tOrders("checkout.cta")}
      </Button>
    ) : undefined;

  // The AR source (#382, ADR-0035 §7): the CURRENT object config the bake flattens —
  // chosen mesh, pet, composited Background, and the physical size (longest FACE side
  // → true metre scale, from the SAME sizeModel readout the caption shows). Gathered
  // at render so a tap always bakes the latest config; transient camera state is not
  // here (the OS re-places the object in a canonical rest pose). Null until READY with
  // a measured size — which also keeps the button hidden. The button is always
  // rendered (it self-hides) and paired ABOVE "Zamów" by StageControls.
  const arSource: ArSource | null =
    ready && meshUrl && readout
      ? {
          meshUrl,
          textureFrontUrl: textureFront,
          background: isRectangle ? RECTANGLE_BACKGROUND_COLOR : "#ffffff",
          targetLongestCm: Math.max(readout.widthCm, readout.heightCm),
        }
      : null;
  const arCta = (
    <ArEntryButton capability={arCapability} flagEnabled={arPreviewEnabled} source={arSource} />
  );

  return (
    // Chrome over the persistent Stage canvas. The middle is left clear so the
    // canvas behind orbits; each control opts back into pointer events.
    <section className="flex h-full flex-col" data-testid="preview-chrome">
      {/* The page's accessible heading. The visible "Podgląd projektu" chip was dropped
          (it added no value on the immersive Stage — Leon, #ui-polish), but the page
          still needs a landmark heading for screen readers + the route-shell contract,
          so it stays as an sr-only h1. */}
      <h1 className="sr-only">{t("shells.preview")}</h1>

      {/* Generation loader: the "we're sewing your pillow" wait state, centred over the
          warm Stage sweep. It carries the live worker stage + a stay-here nudge, then
          fades out as the finished cushion reveals (kept mounted through the fade so the
          loop→reveal handoff crossfades). A soft dark pool sits behind it so the light
          type + cream pillow read over the bright amber centre. */}
      {loaderMounted ? (
        <div
          className={cn(
            "absolute inset-0 z-20 flex items-center justify-center px-6 transition-opacity duration-500 ease-out motion-reduce:transition-none",
            showLoader ? "opacity-100" : "pointer-events-none opacity-0",
          )}
          data-testid="preview-loader"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(46% 42% at 50% 47%, rgba(28,16,6,0.58), transparent 72%)",
            }}
          />
          {/* Sits above the scrim; the loader owns its own pointer-events. */}
          <div className="relative">
            <GenerationLoader
              stage={stream.stage}
              failed={failed}
              error={stream.error}
              onRetry={onRetry}
            />
          </div>
        </div>
      ) : null}

      {/* The controls + dimension caption + legal notice fade in TOGETHER only after
          the reveal settles (#184). A static wrapper (the children stay self-positioned
          absolute against the section), opacity-faded and made inert while hidden so the
          canvas still orbits through it. Snaps (no transition) under reduced motion. */}
      <div
        data-testid="preview-controls-fade"
        className={cn(
          "transition-opacity duration-500 ease-out motion-reduce:transition-none",
          chromeVisible ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        {/* The one flat-chrome controls bar (#163, Option A): pillow shape/size
            settings + the silhouette size slider (#185) — the SAME bar as the upload
            Stage — plus reset-view once the cushion is ready. Self-positioned (bottom
            rail on desktop, slide-out sheet on mobile); it owns its own pointer-events
            so the canvas still takes the drag through the empty space. Reset drives the
            persistent Stage via the store. Turning the cushion is the drag itself — no
            Flip button, no Inspect mode (ADR-0045). */}
        <StageControls
          shape={shape}
          size={size}
          onShape={setShape}
          onSize={setSize}
          silhouetteSizeT={silhouetteSizeT}
          onSilhouetteSize={setSilhouetteSizeT}
          // The S/L threshold line is PHYSICAL (2026-07-12): it follows which shipping box
          // the chosen size fits, from the same readout as the caption (photo-dependent,
          // moves per pet). Null markerT (rectangle / not-yet-measured) parks it at centre.
          // The live price in the "Zamów" CTA is what tells the buyer the large tier costs
          // more — the line just marks where the price steps up.
          thresholdPct={readout?.markerT != null ? readout.markerT * 100 : undefined}
          showActions={ready}
          onResetView={resetView}
          orbitMode={orbitMode}
          onOrbitMode={setOrbitMode}
          // The live cm footprint (#185), now shown INSIDE the bar next to the slider
          // rather than as a separate floating pill (Leon, #ui-polish). Same sizeModel
          // readout as before, so the number can't drift from the applied 3D scale.
          sizeReadoutLabel={readout?.label}
          // The "Zamów" CTA lives in the bar now (desktop: right end; mobile: floating
          // bottom-centre). Undefined until ready, so the bar shows no CTA mid-wait.
          orderCta={orderCta}
          // The secondary "Zobacz w swoim pokoju" AR action (#382), paired above
          // "Zamów". Self-hiding: renders null unless AR-capable + flag on + READY.
          arCta={arCta}
        />

        {/* The legal notice (#167): an ⓘ popover with the faithful-but-not-1:1
            disclaimer + a "see comparisons" link to the comparison page (#164).
            Shown once a cushion is on the Stage; after the Reveal it peeks a short
            line for ~10s then collapses to just the icon (driven by revealPlayToken
            — the same bump that plays the Reveal). Self-positioned bottom-left. */}
        {ready ? <LegalNotice revealToken={revealPlayToken} /> : null}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/preview/$jobId")({
  head: () => ({ meta: [{ title: appShellTitle(i18n.t("shells.preview")) }] }),
  component: PreviewShell,
});
